import { useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getListSnapshotsV1SnapshotsGetQueryKey,
  useCatalogV1SnapshotsCatalogGet,
  useCreateExportV1SnapshotsExportsPost,
  useGetSnapshotV1SnapshotsSnapshotIdGet,
  useListSnapshotsV1SnapshotsGet,
  usePinV1SnapshotsSnapshotIdPinPost,
  useUnpinV1SnapshotsSnapshotIdUnpinPost,
  createImportV1SnapshotsImportsPost,
  importPreflightV1SnapshotsImportsPreflightPost,
} from "../api/generated/qcsControlPlane";
import type { DatasetInfo, Platform, PreflightOut, SnapshotOut } from "../api/generated/model";
import { ApiError } from "../api/mutator";
import { parseTs } from "../api/labels";
import { isPlatform, platformKeys, usePlatforms } from "../api/platforms";
import { type AnyColumnDef, DataTable, createColumnHelper } from "../components/DataTable";
import { useToast } from "../components/toast";
import { useCanWrite, useCurrentUser } from "../auth/me";
import { Badge, Button, Dot, Input, PageHeader, Panel, Row, Stack, type DotTone as Tone } from "@akaike/ui";
import { toneVariant } from "../lib/badge-tone";
import { Seg } from "../components/foundation";

const TERMINAL = new Set(["ready", "done", "failed", "expired"]);

function errMsg(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

const fmtBytes = (n: number) => {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  const i = Math.min(u.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / 1024 ** i).toFixed(i ? 1 : 0)} ${u[i]}`;
};

const fmtTime = (iso: string) =>
  parseTs(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

type Tab = "export" | "import" | "history";

export function Snapshots() {
  const me = useCurrentUser();
  const canWrite = useCanWrite();
  const [tab, setTab] = useState<Tab>(canWrite ? "export" : "history");

  return (
    <>
      <PageHeader
        className="mb-3.5"
        title="Snapshots"
        description={<>Move datasets between deployments (prod &harr; local)</>}
      />

      <Seg
        className="mb-3.5"
        value={tab}
        onChange={setTab}
        options={[
          ...(canWrite ? [{ value: "export" as Tab, label: "Export" }] : []),
          ...(me.is_superuser ? [{ value: "import" as Tab, label: "Import" }] : []),
          { value: "history", label: "History" },
        ]}
      />

      {tab === "export" && canWrite && <ExportPanel />}
      {tab === "import" && me.is_superuser && <ImportPanel />}
      {tab === "history" && <HistoryPanel isSuperuser={me.is_superuser} />}
    </>
  );
}

// -- Export -------------------------------------------------------------------

function ExportPanel() {
  const notify = useToast();
  const qc = useQueryClient();
  usePlatforms(); // ensure the /v1/platforms cache is warm
  const catalog = useCatalogV1SnapshotsCatalogGet();
  const platformOptions = platformKeys();
  const [platformSel, setPlatformSel] = useState<Platform | null>(null);
  const platform = platformSel ?? platformOptions[0] ?? null;
  const [selected, setSelected] = useState<Set<string>>(new Set(["store-locations", "skus"]));
  const [brand, setBrand] = useState("");
  const [store, setStore] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [gzip, setGzip] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);

  // memoised: the `?? []` fallback is a new array each render, which defeats the memos below
  const datasets = useMemo(() => catalog.data?.datasets ?? [], [catalog.data]);
  const presets = catalog.data?.presets ?? {};
  const anyFact = useMemo(
    () => datasets.some((d) => selected.has(d.name) && d.is_fact),
    [datasets, selected],
  );

  const toggle = (name: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const applyPreset = (names: string[]) => setSelected(new Set(names));

  const create = useCreateExportV1SnapshotsExportsPost({
    mutation: {
      onSuccess: (row: SnapshotOut) => {
        setJobId(row.id);
        qc.invalidateQueries({
          queryKey: getListSnapshotsV1SnapshotsGetQueryKey(),
        });
      },
      onError: (e) => notify(errMsg(e, "export failed to start"), "crit"),
    },
  });

  const build = () => {
    if (selected.size === 0) {
      notify("select at least one dataset", "crit");
      return;
    }
    if (platform === null) {
      notify("no platform available", "crit");
      return;
    }
    create.mutate({
      data: {
        datasets: [...selected],
        platform,
        brand_scope: brand.trim() ? brand.split(",").map((b) => b.trim()) : null,
        store_scope: store.trim() ? store.split(",").map((s) => s.trim()) : null,
        since: since || null,
        until: until || null,
        gzip,
      },
    });
  };

  return (
    <div
      className="grid"
      style={{
        gridTemplateColumns: "repeat(12,1fr)",
        alignItems: "start",
        gap: 14,
      }}
    >
      <Panel style={{ gridColumn: "span 8" }}>
        <Panel.Head title="Build a bundle" />
        <Panel.Body>
          <Stack gap="md">
            <label className="col gap-1">
              <span className="muted">Platform</span>
              <select className="input" value={platform ?? ""}
                onChange={(e) => {
                  if (isPlatform(e.target.value)) setPlatformSel(e.target.value);
                }}
              >
                {platformOptions.map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </label>

            <Stack gap="xs">
              <span className="muted">Datasets</span>
              {datasets.map((d: DatasetInfo) => (
                <label key={d.name} className="row" style={{ gap: 8, alignItems: "flex-start" }}>
                  <input type="checkbox" checked={selected.has(d.name)} onChange={() => toggle(d.name)} />
                  <span className="col gap-px">
                    <b>
                      {d.name} {d.is_fact && <Badge variant="outline-warning">fact</Badge>}
                    </b>
                    <span className="muted" style={{ fontSize: 12 }}>
                      {d.description}
                    </span>
                  </span>
                </label>
              ))}
            </Stack>

            <Row gap="sm">
              <span className="muted">Quick bundles:</span>
              {Object.entries(presets).map(([name, names]) => (
                <Button key={name} size="sm" onClick={() => applyPreset(names)}>
                  {name}
                </Button>
              ))}
            </Row>

            <label className="col gap-1">
              <span className="muted">Brand scope (comma-separated, optional)</span>
              <Input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="all" />
            </label>

            <label className="col gap-1">
              <span className="muted">Store scope (store ids, comma-separated, optional)</span>
              <Input value={store} onChange={(e) => setStore(e.target.value)} placeholder="all" />
            </label>

            {anyFact && (
              <Row gap="sm">
                <label className="col gap-1">
                  <span className="muted">Since (fact window)</span>
                  <Input type="datetime-local" value={since} onChange={(e) => setSince(e.target.value)} />
                </label>
                <label className="col gap-1">
                  <span className="muted">Until</span>
                  <Input type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} />
                </label>
              </Row>
            )}

            <label className="row" style={{ gap: 8 }}>
              <input type="checkbox" checked={gzip} onChange={(e) => setGzip(e.target.checked)} />
              <span>gzip archive (instead of zstd)</span>
            </label>

            <Row gap="sm">
              <Button variant="primary" onClick={build} disabled={create.isPending}>
                {create.isPending ? "Starting…" : "Build & download"}
              </Button>
            </Row>
          </Stack>
        </Panel.Body>
      </Panel>

      <Stack gap="lg" style={{ gridColumn: "span 4" }}>
        {jobId && <JobProgress id={jobId} />}
      </Stack>
    </div>
  );
}

function JobProgress({ id }: { id: string }) {
  const q = useGetSnapshotV1SnapshotsSnapshotIdGet(id, {
    query: {
      refetchInterval: (query) => (TERMINAL.has(query.state.data?.status ?? "") ? false : 1500),
    },
  });
  const row = q.data;
  if (!row) return null;
  const downloadable = row.kind === "export" && row.status === "ready";
  return (
    <Panel>
      <Panel.Head title={<>{row.kind === "import" ? "Import" : "Export"} {row.status}</>} />
      <Panel.Body>
        <Stack gap="sm">
          <StatusPill status={row.status} />
          <div className="muted" style={{ fontSize: 12 }}>
            {row.datasets.join(", ")}
          </div>
          {row.error && <Badge variant="outline-destructive">{row.error}</Badge>}
          {downloadable && (
            <>
              <div className="num">
                {row.row_count.toLocaleString()} rows · {fmtBytes(row.bytes)}
              </div>
              <Button asChild variant="primary">
                <a href={`/v1/snapshots/${row.id}/download`}>Download bundle</a>
              </Button>
            </>
          )}
        </Stack>
      </Panel.Body>
    </Panel>
  );
}

// -- Import (superuser) -------------------------------------------------------

function ImportPanel() {
  const notify = useToast();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState("auto");
  const [preflight, setPreflight] = useState<PreflightOut | null>(null);
  const [busy, setBusy] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);

  // The generated client builds the same multipart body (payload + file); it also
  // unwraps FastAPI's `detail` into ApiError.message and keeps the status, which
  // the production-confirm retry below branches on.
  const body = (confirm = false) => {
    const file = fileRef.current?.files?.[0];
    if (!file) throw new Error("choose a bundle archive first");
    return { payload: JSON.stringify({ mode, confirm }), file };
  };

  const runPreflight = async () => {
    setBusy(true);
    setPreflight(null);
    try {
      setPreflight(await importPreflightV1SnapshotsImportsPreflightPost(body()));
    } catch (e) {
      notify(errMsg(e, "preflight failed"), "crit");
    } finally {
      setBusy(false);
    }
  };

  const runImport = async () => {
    setBusy(true);
    try {
      // First attempt WITHOUT confirm so the server's production guard can trip; only
      // then ask the operator to confirm and retry (honours the prod confirm).
      let snapshot: SnapshotOut;
      try {
        snapshot = await createImportV1SnapshotsImportsPost(body(false));
      } catch (e) {
        if (!(e instanceof ApiError && e.status === 400 && e.message.includes("production"))) {
          throw e;
        }
        if (!window.confirm(`${e.message}\n\nProceed with the import?`)) {
          setBusy(false);
          return;
        }
        snapshot = await createImportV1SnapshotsImportsPost(body(true));
      }
      setJobId(snapshot.id);
      qc.invalidateQueries({
        queryKey: getListSnapshotsV1SnapshotsGetQueryKey(),
      });
      notify("import started", "ok");
    } catch (e) {
      notify(errMsg(e, "import failed"), "crit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="grid"
      style={{
        gridTemplateColumns: "repeat(12,1fr)",
        alignItems: "start",
        gap: 14,
      }}
    >
      <Panel style={{ gridColumn: "span 8" }}>
        <Panel.Head title="Import a bundle">
          <div className="flex-1" />
          <Badge variant="outline-warning">superuser</Badge>
        </Panel.Head>
        <Panel.Body>
          <Stack gap="md">
            <label className="col gap-1">
              <span className="muted">Bundle archive (.tar.zst / .tar.gz)</span>
              <Input ref={fileRef} type="file" accept=".zst,.gz,.tar" />
            </label>
            <label className="col gap-1">
              <span className="muted">Mode</span>
              <select className="input" value={mode} onChange={(e) => setMode(e.target.value)}>
                <option value="auto">auto (dims replace-if-non-empty; facts append)</option>
                <option value="append">append</option>
                <option value="replace">replace (window-scoped for facts)</option>
              </select>
            </label>
            <Row gap="sm">
              <Button onClick={runPreflight} disabled={busy}>
                Preflight
              </Button>
            </Row>

            {preflight && (
              <Panel className="mt-1">
                <Panel.Head title="Preflight">
                  <div className="flex-1" />
                  {preflight.ok ? <Badge variant="outline-success">safe to import</Badge> : <Badge variant="outline-destructive">blocked</Badge>}
                </Panel.Head>
                <Panel.Body>
                  <Stack gap="sm">
                    <div className="muted" style={{ fontSize: 12 }}>
                      platform {String(preflight.manifest.platform)} · datasets{" "}
                      {(preflight.manifest.datasets as string[])?.join(", ")}
                    </div>
                    {preflight.warnings.map((w, i) => (
                      <Badge key={i} variant="outline-warning">
                        {w}
                      </Badge>
                    ))}
                    <Badge variant="outline-warning">
                      Quiesce the target: do not dispatch flows against this platform during import.
                    </Badge>
                    {preflight.ok && (
                      <Button variant="primary" onClick={runImport} disabled={busy}>
                        Confirm import
                      </Button>
                    )}
                  </Stack>
                </Panel.Body>
              </Panel>
            )}
          </Stack>
        </Panel.Body>
      </Panel>

      <Stack gap="lg" style={{ gridColumn: "span 4" }}>
        {jobId && <JobProgress id={jobId} />}
      </Stack>
    </div>
  );
}

// -- History ------------------------------------------------------------------

function HistoryPanel({ isSuperuser }: { isSuperuser: boolean }) {
  const list = useListSnapshotsV1SnapshotsGet({
    query: { refetchInterval: 3000 },
  });
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({
      queryKey: getListSnapshotsV1SnapshotsGetQueryKey(),
    });
  const pin = usePinV1SnapshotsSnapshotIdPinPost({
    mutation: { onSuccess: invalidate },
  });
  const unpin = useUnpinV1SnapshotsSnapshotIdUnpinPost({
    mutation: { onSuccess: invalidate },
  });
  const rows = list.data ?? [];

  const columns = useMemo<AnyColumnDef<SnapshotOut>[]>(() => {
    const col = createColumnHelper<SnapshotOut>();
    return [
      col.accessor("created_at", {
        header: "When",
        cell: ({ row }) => fmtTime(row.original.created_at),
        meta: { width: 150, exportValue: (r) => r.created_at },
      }),
      col.accessor("kind", {
        header: "Kind",
        meta: { width: 90, filterSelect: true, filterLabel: "Kind" },
      }),
      col.accessor("platform", {
        header: "Platform",
        cell: ({ row }) => row.original.platform ?? "",
        meta: { width: 110, filterSelect: true, filterLabel: "Platform" },
      }),
      col.accessor((r) => r.datasets.join(", "), {
        id: "datasets",
        header: "Datasets",
        cell: ({ row }) => <span className="muted">{row.original.datasets.join(", ")}</span>,
        meta: { minWidth: 200 },
      }),
      col.accessor("row_count", {
        header: "Rows",
        cell: ({ row }) => (row.original.row_count ? row.original.row_count.toLocaleString() : "—"),
        meta: { align: "right", width: 100, cellClassName: "num" },
      }),
      col.accessor("created_by", {
        header: "By",
        cell: ({ row }) => <span className="muted">{row.original.created_by}</span>,
        meta: { minWidth: 140 },
      }),
      col.accessor("status", {
        header: "Status",
        cell: ({ row }) => <StatusPill status={row.original.status} />,
        meta: { width: 130, filterSelect: true, filterLabel: "Status" },
      }),
      col.display({
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const r = row.original;
          return (
            <Row gap="xs" justify="end">
              {r.kind === "export" && r.status === "ready" && (
                <Button asChild size="sm">
                  <a href={`/v1/snapshots/${r.id}/download`}>Download</a>
                </Button>
              )}
              {isSuperuser &&
                r.kind === "export" &&
                (r.pinned ? (
                  <Button size="sm" onClick={() => unpin.mutate({ snapshotId: r.id })}>
                    Unpin
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => pin.mutate({ snapshotId: r.id })}>
                    Pin
                  </Button>
                ))}
            </Row>
          );
        },
        meta: { align: "right", width: 150 },
        enableSorting: false,
        enableHiding: false,
      }),
    ];
  }, [isSuperuser, pin, unpin]);

  return (
    <Panel>
      <Panel.Head title="History" />
      <Panel.Body tight>
        <DataTable
          tableKey="snapshots"
          fillHeight
          columns={columns}
          data={rows}
          getRowId={(r) => r.id}
          globalPlaceholder="Search snapshots…"
          csvFilename="snapshots"
          emptyMessage="No snapshots yet."
          isLoading={list.isLoading}
        />
      </Panel.Body>
    </Panel>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone: Tone | undefined =
    status === "ready" || status === "done"
      ? "ok"
      : status === "failed"
        ? "crit"
        : status === "expired"
          ? undefined
          : "warn";
  return (
    <Badge variant={toneVariant(tone)}>
      <Dot tone={tone} /> {status}
    </Badge>
  );
}
